//July28 src/components/Navbar/NavbarComponent.jsx:
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '../../utils/authFunctions'; // useLogout for handling logout logic
import { AppBar, Toolbar, Button, Box, IconButton, Menu, MenuItem, Avatar, Typography } from '@mui/material';
import { useBillings } from '../../contexts/BillingContext';
import TrialBanner from "../Billing/TrialBanner";
import ScheduledChangeBanner from "../Billing/ScheduledChangeBanner";

import PublicIcon         from '@mui/icons-material/Public';
import LockOutlinedIcon   from '@mui/icons-material/LockOutlined'; 
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'; 
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareForm      from '../Share/ShareForm';
import useDuplicateForm  from '../../hooks/useDuplicateForm';
import Dialog         from '@mui/material/Dialog';   // modal wrapper
import { useAuth }    from '../../contexts/AuthContext';
import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import menuStyles from '../../styles/menuStyles'; // Import menu styles
import { useFormContext } from '../../contexts/FormContext';
import DuplicateSuccessModal  from '../Dialogs/DuplicateSuccessModal';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import CircularProgress from '@mui/material/CircularProgress';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../../utils/storageUtils';
import { setAufPendingFile } from '../../utils/aufHandoffStore';
import { useNavbarActions } from '../../contexts/NavbarActionsContext';

/* ──────────────────────────────────────────────────────────────
   Safe wrapper: returns inert helpers when FormContext missing.
   ────────────────────────────────────────────────────────────── */
function useDuplicateFormSafe() {
  const formCtx = useFormContext();            // undefined on Home, Settings, …
  if (!formCtx) {
    /* Return dummies so callers can de-structure safely */
    return {
      handleDuplicateForm  : () => {},
      duplicateModalState  : null,
      dismissDuplicateModal: () => {},
      goToDuplicatedForm   : () => {},
    };
  }
  /* Normal behaviour inside builder / /forms/:id routes */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useDuplicateForm();

}


// Styled AppBar for consistent UI
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#FFFFFF',
  color: '#000000',
  boxShadow: 'none',
  borderBottom: '2px solid #D3D3D3',
});

// Styled Button to maintain UI consistency
const StyledButton = styled(Button)({
  textTransform: 'none',
  margin: '0 12px',
  borderRadius: '4px',
});

function NavbarComponent({ currentUser }) {
//  console.log('[NavbarComponent] Current user:', currentUser); // Add this log to track currentUser

  const logout = useLogout(); // Keeping useLogout for the logout logic
  const { actions: navbarActions } = useNavbarActions();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [showShareForm, setShowShareForm] = useState(false);
  const { hasPermission, organizationId } = useAuth();
  const canShare = hasPermission('shareForm');   // editors / admins / owners

  const canDuplicate = hasPermission('createForms');   // creators / admins
  const canPay       = hasPermission('manageBilling');      // owners + billingManager

  /* ------------ form context (must come first) --------------- */
  const { formId, formTitle, formDetails } = useFormContext() || {};

  /* optional: only if you still need it elsewhere */
  const isOwner = currentUser?.uid === formDetails?.owner;

const {
  handleDuplicateForm,
  duplicateModalState,
  goToDuplicatedForm,
  dismissDuplicateModal,
} = useDuplicateFormSafe(); 

  const navigate  = useNavigate();
  const location  = useLocation();
  const navbarPickerRef = React.useRef(null);

  // ─────────────────────────────────────────────────────────────
  // AUF pointer preload (only show +Upload if AUF is PUBLISHED)
  // ─────────────────────────────────────────────────────────────
  const [hasPublishedAuf, setHasPublishedAuf] = useState(false);
  const [aufId, setAufId] = useState(null);
  const [launchingAuf, setLaunchingAuf] = useState(false);

  useEffect(() => {
    let canceled = false;
    const ORG_KEY = organizationId ? `org_${organizationId}_aufPtr` : null;
    const TTL_MS = 10 * 60 * 1000; // 10 minutes

    (async () => {
      setHasPublishedAuf(false);
      setAufId(null);
      if (!organizationId) return;

      // 1) Try cache first (safeGetItem returns the *value* we stored)
      if (ORG_KEY) {
        const cached = safeGetItem(ORG_KEY); // { aufId, verified, ts } | null
        if (cached?.verified && cached?.aufId && (Date.now() - (cached.ts || 0) < TTL_MS)) {
          if (!canceled) {
            setAufId(cached.aufId);
            setHasPublishedAuf(true);
          }
          return; // cache hit
        }
      }

      // 2) Resolve pointer & verify published AUF
      try {
        const orgSnap = await getDoc(doc(db, 'organizations', organizationId));
        const pointer = orgSnap.exists() ? (orgSnap.data()?.assetUploadFormId || null) : null;
        if (!pointer) {
          if (ORG_KEY) safeRemoveItem(ORG_KEY); // clear any stale cache
          return;
        }

        const formSnap = await getDoc(doc(db, 'forms', pointer));
        const isPublished =
          formSnap.exists() &&
          formSnap.data()?.isAssetUploadForm === true &&
          formSnap.data()?.status === 'published';

        if (!canceled && isPublished) {
          setAufId(pointer);
          setHasPublishedAuf(true);
          if (ORG_KEY) {
            // 3) Cache via storageUtils with an internal timestamp
            safeSetItem(ORG_KEY, { aufId: pointer, verified: true, ts: Date.now() });
          }
        } else if (ORG_KEY) {
          safeRemoveItem(ORG_KEY);
        }
      } catch (e) {
        console.warn('[Navbar] AUF pointer check failed:', e);
        // don’t write cache on error
      }
    })();

    return () => { canceled = true; };
  }, [organizationId]);


  /**
   * `NavbarComponent` also renders on pages that aren't wrapped in
   * <BillingsProvider>.  `useBillings()` will simply return `undefined`
   * in that scenario, which we can safely handle with optional chaining.
   * Calling the hook **unconditionally** keeps ESLint happy.
   */
  const billingsCtx           = useBillings();   // may be undefined

  const showNavUpgrade        = billingsCtx?.showNavUpgrade ?? false;
  const currentSubscription   = billingsCtx?.currentSubscription ?? null;
  const createCheckoutSession = billingsCtx?.createCheckoutSession
                                ?? (async () => null);   // no-op when absent


  const handleNavbarUpgrade = useCallback(async () => {
    /* use current seat count if we have it; harmless fallback to 1 */
    const seatCount = currentSubscription?.seats ?? 1;
    try {
      const url = await createCheckoutSession({ seatCount });
      if (url) {
        window.location.href = url;
        return;
      }
    } catch (err) {
      console.error('[Navbar] upgrade CTA failed', err);
    }
    navigate('/subscription', { state: { from: location.pathname } });
  }, [createCheckoutSession, currentSubscription?.seats, navigate, location.pathname]);

  // ─────────────────────────────────────────────────────────────
  // “+ Upload” → open AUF modal (only if published; else fallback)
  // ─────────────────────────────────────────────────────────────
  const handleOpenUpload = useCallback(async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!organizationId) {
      navigate('/settings/asset-studio');
      return;
    }
    try {
      setLaunchingAuf(true);
      if (hasPublishedAuf && aufId) {
        navigate(`/forms/${aufId}`, { state: { modalFrom: location } });
      } else {
        // Safety fallback (shouldn’t happen if button is hidden correctly)
        navigate('/settings/asset-studio');
      }
    } finally {
      setLaunchingAuf(false);
    }
  }, [currentUser, organizationId, hasPublishedAuf, aufId, navigate, location]);

  /**
   * Navbar picker → user chose a file
   * Stash the File in memory and open AUF dialog.
   */
  const handleNavbarPickChange = React.useCallback((e) => {
    try {
      const f = e.target.files?.[0] || null;
      // reset so the same file can be chosen again later
      if (navbarPickerRef.current) navbarPickerRef.current.value = '';
      if (!f) return; // user canceled

      // 1) stash in-memory so AUF can consume immediately on mount
      setAufPendingFile(f);

      // 2) optional: tiny bridge so AUF won’t re-prompt picker
      //    We don’t have an origin form here → originFormId = null
      safeSetItem('navbar_auf_bridge', {
        originFormId : null,
        originRoute  : location.pathname,
        requestPick  : false,      // we already have a file
        createdAt    : Date.now(),
      });

      // 3) open AUF dialog over current page
      if (hasPublishedAuf && aufId) {
        navigate(`/forms/${aufId}`, { state: { modalFrom: location } });
      }
    } catch (err) {
      console.warn('[Navbar] handleNavbarPickChange failed:', err);
    }
  }, [hasPublishedAuf, aufId, navigate, location]);


// Narrow route detection: viewer vs. any /forms/*
const isFormsAnyRoute = location.pathname.startsWith('/forms/');
const isPublishedFormViewerRoute = /^\/forms\/[^/]+$/.test(location.pathname); // exactly /forms/:id
const isFormBuilderRoute   = location.pathname.startsWith('/form-builder/');
// Asset Details (exact /assets/:id)
const isAssetDetailsRoute = /^\/assets\/[^/]+$/.test(location.pathname);

// Show the global Upload button only on non-form routes (e.g. Home/Settings),
// not on /forms/:id (viewer) nor /form-builder/:id (builder).
// Also hide Upload on Asset Details (we’ll show Edit/Done there)
const showGlobalUpload = currentUser && hasPublishedAuf && !isFormsAnyRoute && !isFormBuilderRoute && !isAssetDetailsRoute;

// Edit permission for Asset Details screen
const canEditAssetDetails =
  currentUser &&
  (hasPermission('uploadAsset') || hasPermission('editUploadAssetForm'));

// ───────────────────────────────────────────────────────────────
// SmartLists navbar action bridge
// - only active on /settings/smartlists
// - action state is published by SmartListsPage via NavbarActionsContext
// - Save is a workspace/page action, not a per-group action
// ───────────────────────────────────────────────────────────────
const isSmartListsRoute = location.pathname === '/settings/smartlists';

const showSmartListsSave =
  isSmartListsRoute &&
  currentUser &&
  navbarActions?.pageKey === 'smartlists' &&
  typeof navbarActions?.primaryActionOnClick === 'function';

const smartListsSaveLabel =
  navbarActions?.primaryActionLabel || 'Save';

const smartListsSaveEnabled =
  navbarActions?.primaryActionEnabled === true;

const smartListsSaveLoading =
  navbarActions?.primaryActionLoading === true;

const smartListsDirty =
  navbarActions?.dirty === true;

/* ──────────────────────────────────────────────────────────────
   What level of sharing applies to this form?
   • 'public'      – anyone-with-link
   • 'org'         – visible to entire organisation
   • 'restricted'  – invitation-only
   • null          – nothing to show (anonymous on non-public form, etc.)
   ────────────────────────────────────────────────────────────── */
const shareLevel = useMemo(() => {
  if (!formDetails) return null;

  /*  Public link — always visible  */
  if (formDetails.isPublic) return 'public';

  /*  Org-wide / Restricted only matter when the viewer is logged-in  */
  if (!currentUser) return null;

  if (formDetails.orgAccess && formDetails.orgAccess !== 'none') return 'org';

  return 'restricted';
}, [
  formDetails?.isPublic,
  formDetails?.orgAccess,
  currentUser,
]);

/* Show the badge only on …/forms/:id routes *and* when shareLevel exists */
// ───────────────────────────────────────────────────────────────
//  Share-status indicator (always shown on /forms/:id when ready)
//  • Public          → 🌐  PublicIcon            (isPublic === true)
//  • Org-wide        → 🏢  DomainOutlinedIcon    (orgAccess !== 'none')
//  • Restricted      → 🔒  LockOutlinedIcon
// ───────────────────────────────────────────────────────────────

/* Show the badge only on …/forms/:id routes *and* when shareLevel exists */
// ───────────────────────────────────────────────────────────────
//  Share-status indicator… (existing code)
// ───────────────────────────────────────────────────────────────

let shareLabel = null;
let ShareIcon  = null;

if (isPublishedFormViewerRoute && formDetails) {
  if (formDetails.isPublic) {
    shareLabel = 'Public';
    ShareIcon  = PublicIcon;
  } else if (formDetails.orgAccess && formDetails.orgAccess !== 'none') {
    shareLabel = 'Org';
    ShareIcon  = DomainOutlinedIcon;
  } else {
    shareLabel = 'Restricted';
    ShareIcon  = LockOutlinedIcon;
  }
}

/* ──────────────────────────────────────────────────────────────
   AUF marker – only for the special Asset Upload Form
   (we’ll use this to suppress Copy on full-page AUF viewer)
   ─────────────────────────────────────────────────────────── */
const isAssetUpload =
  formDetails?.isAssetUploadForm === true ||
  !!formDetails?.assetIngestConfig ||
  formDetails?.formType === 'assetUpload';


/* --------------------------------------------------------------
   Should we render the “Copy” button?
   • must be on  /forms/:id
   • user must be logged-in and have createForms permission
   • duplication helpers must be live (inside FormContext)
   -------------------------------------------------------------- */
const showCopyButton =
  isPublishedFormViewerRoute &&
  currentUser &&
  canDuplicate &&
  typeof handleDuplicateForm === 'function' &&
  !isAssetUpload;


  // Handle opening the profile menu
  const handleProfileMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // Handle closing the profile menu
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Navigate to settings
  const handleSettingsClick = useCallback(() => {
    navigate('/settings');
    handleMenuClose();
  }, [navigate, handleMenuClose]);

  // Handle logout functionality
  const handleLogoutClick = useCallback(() => {
    logout();
    handleMenuClose();
  }, [logout, handleMenuClose]);

  // Handle navigating back to the previous page
  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Get a random color for user avatar background
  const getRandomColor = useCallback(() => {
    const colors = [
      '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3',
      '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
      '#FFEB3B', '#FFC107', '#FF9800', '#FF5722'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);

  const userInitial = useMemo(() => currentUser?.email?.charAt(0).toUpperCase(), [currentUser]); // Get the initial letter of the user's email

  const menuId = 'primary-account-menu'; // Menu ID for accessibility

  /* ─────────────────────────────────────────────────────────────
     Asset Details — Navbar ↔ Page bridge
     - listen for "assetdetails:status" → { editing, canEdit, route, assetId }
     - keep a local isAssetEditing flag so our button label toggles
     - we’ll dispatch "assetdetails:set-edit" from the button onClick
     ───────────────────────────────────────────────────────────── */
  const [isAssetEditing, setIsAssetEditing] = useState(false);

  useEffect(() => {
    const onStatus = (e) => {
      try {
        const d = e?.detail || {};
        // Only react on the asset details route
        if (d?.route === 'asset-details') {
          // eslint-disable-next-line no-console
          console.log('[Navbar] assetdetails:status →', d);
          setIsAssetEditing(!!d.editing);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Navbar] assetdetails:status handler failed:', err?.message || err);
      }
    };
    window.addEventListener('assetdetails:status', onStatus);
    return () => window.removeEventListener('assetdetails:status', onStatus);
  }, []);

  // Rendering the dropdown menu for settings and logout
  const renderMenu = useCallback(() => (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{ style: menuStyles.menuPaper }}

      /* 🆕  Make the whole list “dense” and shrink text via MenuListProps  */
      MenuListProps={{
        dense: true,                   // 32-px rows instead of 48 px
        sx: {
          '& .MuiMenuItem-root': {
            fontSize: '0.75rem',       // ≈ 12 px text
          },
        },
      }}
     >
      <MenuItem onClick={handleSettingsClick}>Settings</MenuItem>
      <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
     </Menu>
   ), [anchorEl, isMenuOpen, handleMenuClose, handleSettingsClick, handleLogoutClick]);

  const isNotHomePage = location.pathname !== '/';

  const currentPageName = useMemo(() => {
    // 1️⃣ If we’re on a published-form route and have a title, prefer it.
    if (location.pathname.startsWith('/forms/') && formTitle) {
      return formTitle;
    }

    // 2️⃣ For builder routes (/form-builder/:id/...), ignore the UUID segment.
    if (location.pathname.startsWith('/form-builder/')) {
      const segments = location.pathname.split('/').filter(Boolean);
      const maybeUuid = segments[1];
      const subPage   = segments[2] || 'Design';

      // If subPage is missing (bare `/form-builder/:id`), call it “Design”.
      // Capitalize first letter for display.
      return subPage.charAt(0).toUpperCase() + subPage.slice(1);
    }

    // 3️⃣ Fall back to Home or last URL segment.
    if (location.pathname === '/') return 'Home';

    const lastSegment = location.pathname.split('/').filter(Boolean).pop();

    // If last segment *looks* like a UUID and we have a formTitle, use it.
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(lastSegment) && formTitle) return formTitle;

    // Otherwise cleanly capitalize the segment (e.g. “settings” → “Settings”)
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  }, [location.pathname, formTitle]);

  return (
    <>
      <StyledAppBar position="sticky" elevation={0} className="my-app-bar">
      <Toolbar>
        {/* Display back button on pages other than Home */}
        {currentUser && isNotHomePage && (
          <>
            <IconButton onClick={handleBackClick} edge="start">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ width: 2, bgcolor: '#D3D3D3', mx: 1, alignSelf: 'stretch', marginRight: '20px' }} />
          </>
        )}

        {/* Display the logo and current page name */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <ButtonBase onClick={() => navigate('/')}>
            <img src="/utmlogo.svg" alt="Logo" width={60} height={60} />
          </ButtonBase>
          <div style={{ width: '1px', height: '32px', backgroundColor: '#D3D3D3', marginLeft: '20px', marginRight: '5px' }}></div>
          <Typography variant="h6" sx={{ marginLeft: '20px', flexGrow: 1 }}>{currentPageName}</Typography>
        </Box>

{/* Right-hand controls */}
<Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', flexShrink: 0, mx: '-6px' }}>

  {/* ── ①  Form-Share-Status badge (all published forms) */}
  {shareLevel && ShareIcon && (
<StyledButton
  size="small"
  startIcon={<ShareIcon fontSize="small" />}
  variant="outlined"
  // When Copy is hidden, leave some space before the avatar.
  // When Copy is visible, keep a small gap between Share and Copy.
  sx={{ mr: showCopyButton ? 0.5 : 1.5 }}
      aria-label={`Form share status: ${shareLevel}`}
      disabled={
        !currentUser ||
        !canShare   ||
        (shareLevel === 'public' && !isOwner)   // ⬅ non‑owners on Public form
      }

      onClick={() => currentUser && canShare && setShowShareForm(true)}
    >
      {shareLevel === 'public'
        ? 'Public'
        : shareLevel === 'org'
        ? 'Org-Wide'
        : 'Restricted'}
    </StyledButton>
  )}

  {/* ── ②  Duplicate (visible only with permission) */}
  {showCopyButton && (
    <StyledButton
     size="small"
      startIcon={<FileCopyOutlinedIcon fontSize="small" />}
      variant="contained"
      color="primary"
      sx={{
        textTransform: 'none',
        //mr: 1,                         // gap before avatar
        boxShadow: 'none',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: 'primary.dark',
          boxShadow: 'none',
        },
        '&:active': {
          backgroundColor: 'primary.main',
          boxShadow: 'none',
          transform: 'scale(0.98)',
        },
      }}
      onClick={() => handleDuplicateForm(formId)}
    >
      Copy
    </StyledButton>
  )}

  {/* ── Asset Details: Edit / Done (replaces Upload on /assets/:id) */}
  {isAssetDetailsRoute && canEditAssetDetails && (
    <StyledButton
      variant={isAssetEditing ? 'outlined' : 'contained'}
      size="small"
      onClick={() => {
        try {
          const next = !isAssetEditing;
          // eslint-disable-next-line no-console
          console.log('[Navbar] dispatch assetdetails:set-edit →', next);
          window.dispatchEvent(new CustomEvent('assetdetails:set-edit', { detail: { value: next } }));
          setIsAssetEditing(next); // optimistic toggle; page will confirm via assetdetails:status
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[Navbar] set-edit dispatch failed:', e?.message || e);
        }
      }}
      aria-label={isAssetEditing ? 'Done editing' : 'Edit asset metadata'}
      sx={{
        textTransform: 'none',
        boxShadow: 'none',
        borderRadius: '4px',
        ml: 1.5,
      }}
    >
      {isAssetEditing ? 'Done' : 'Edit'}
    </StyledButton>
  )}

  {/* ── SmartLists Save (workspace-level, route-specific) */}
  {showSmartListsSave && (
    <StyledButton
      size="small"
      startIcon={
        smartListsSaveLoading
          ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
          : <SaveIcon fontSize="small" />
      }
      variant="contained"
      color="primary"
      disabled={!smartListsSaveEnabled || smartListsSaveLoading}
      onClick={navbarActions.primaryActionOnClick}
      aria-label={
        smartListsDirty
          ? 'Save SmartLists changes'
          : 'No SmartLists changes to save'
      }
      sx={{
        textTransform: 'none',
        boxShadow: 'none',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: 'primary.dark',
          boxShadow: 'none',
        },
        '&:active': {
          backgroundColor: 'primary.main',
          boxShadow: 'none',
          transform: 'scale(0.98)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255,255,255,0.70)',
          backgroundColor: 'rgba(25, 118, 210, 0.45)',
        },
      }}
    >
      {smartListsSaveLabel}
    </StyledButton>
  )}

  {/* ── + Upload (only when org has a PUBLISHED AUF) */}
  {showGlobalUpload && (
    <StyledButton
      variant="outlined"
     size="small"
     startIcon={launchingAuf ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
    onClick={() => {
      // picker-first; if user cancels, do nothing
      if (navbarPickerRef.current) {
        navbarPickerRef.current.click();
      } else {
        // Shouldn’t happen, but fall back to the previous behavior
        handleOpenUpload();
      }
    }}

      disabled={launchingAuf}
//      sx={{ mr: 1 }}
      aria-label="Open asset upload form"
    >
      Upload
    </StyledButton>
  )}

 {/* Hidden picker for navbar “Upload” (picker-first) */}
{showGlobalUpload && (
  <input
    ref={navbarPickerRef}
    type="file"
    accept="image/*"
    style={{ display: 'none' }}
    onChange={handleNavbarPickChange}
  />
)}


{/* 🆕  Trial-only “Upgrade” CTA (navbar) */}
 {currentUser && canPay && showNavUpgrade && (
  <StyledButton
    variant="contained"
    size="small"                 // height matches other controls
    onClick={handleNavbarUpgrade}
    sx={{
      textTransform : "none",
      fontWeight    : 600,
      px            : 2,         // ⬅︎ wider (theme ×8 = 16 px)
      minWidth      : 96,        // ⬅︎ 44 dp tap target guarantee
//      mr            : 1,
      borderRadius  : 1,         // = 8 px theme radius
      boxShadow     : "none",
      backgroundColor: "#1976d2",
      color         : "#ffffff",
      transition    : "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "#2F855A",   // PlanCard green
        boxShadow      : "none",
      },
      "&:active": {
        backgroundColor: "#276749",
        boxShadow      : "none",
        transform      : "scale(0.98)",
      },
    }}
  >
    Upgrade
  </StyledButton>
)}



   {/* ── ③  Anonymous-user buttons (unchanged) */}

  {!currentUser && (
    <>
      {!shareLevel && (                           /* avoid duplicate badge */
        <StyledButton startIcon={<PublicIcon />} variant="outlined">
          Public
        </StyledButton>
      )}
      <StyledButton
        variant="contained"
        color="primary"
        sx={{
          textTransform : 'none',
          boxShadow     : 'none',
          '&:hover'     : { backgroundColor: 'primary.dark', boxShadow: 'none' },
          '&:active'    : { backgroundColor: 'primary.main', boxShadow: 'none' },
        }}
        onClick={() => navigate('/login')}
      >
        Log In
      </StyledButton>

    </>
  )}

  {/* ── ③  Avatar (shown when logged-in) */}
  {currentUser && (
    <IconButton
      edge="end"
      aria-label="account of current user"
      aria-controls={menuId}
      aria-haspopup="true"
      onClick={handleProfileMenuOpen}
      color="inherit"
    >
      <Avatar sx={{ bgcolor: getRandomColor() }}>{userInitial}</Avatar>
    </IconButton>
  )}
</Box>


{/* ───────────────────────────────────────────────
    Share-permissions dialog (opens from the badge)
    ─────────────────────────────────────────────── */}
<Dialog
  open={showShareForm}
  onClose={() => setShowShareForm(false)}
  fullWidth
  maxWidth="sm"
>
  <Box sx={{ p: 3 }}>
    <ShareForm
      formName={formTitle}
      formId={formId}
      formUrl={`/forms/${formId}`}
      formStatus="published"
      isPublic={formDetails?.isPublic}
      orgAccess={formDetails?.orgAccess}
      sharedWith={formDetails?.sharedWith}
      onClose={() => setShowShareForm(false)}
    />
  </Box>
</Dialog>

{duplicateModalState && (
  <DuplicateSuccessModal
    open={duplicateModalState.open}
    onClose={dismissDuplicateModal}
    onNavigate={goToDuplicatedForm}
  />
)}

        {renderMenu()}
      </Toolbar>
      </StyledAppBar>
      <ScheduledChangeBanner />
      <TrialBanner />
    </>

  );
}

// Memoize the entire component to avoid unnecessary re-renders
export default React.memo(NavbarComponent);
